import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useAuthStore } from "./store";
import * as authApi from "./api";
import { createLogger } from "@/shared/lib/logger";
import type { LoginRequest, RegisterRequest } from "./types";

const log = createLogger("AUTH_HOOKS");

export function useRegister() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: RegisterRequest) => {
      log.info("Register + auto-login flow started");
      await authApi.register(data);
      return authApi.login({ login: data.login, password: data.password });
    },
    onSuccess: (res) => {
      setAuth({
        access: res.auth.token,
        refresh: res.auth.refresh_token,
        account: res.account,
        roles: res.roles,
        user: res.user,
        sessionId: res.session_id,
      });
      log.info("Register + login success, navigating to dashboard");
      navigate("/dashboard", { replace: true });
    },
  });
}

export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (res) => {
      setAuth({
        access: res.auth.token,
        refresh: res.auth.refresh_token,
        account: res.account,
        roles: res.roles,
        user: res.user,
        sessionId: res.session_id,
      });
      log.info("Login success, navigating to dashboard");
      navigate("/dashboard", { replace: true });
    },
  });
}

export function useLogout() {
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clearStore = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      authApi.logout({ refresh_token: refreshToken ?? undefined }),
    onSettled: () => {
      clearStore();
      queryClient.clear();
      log.info("Logged out, navigating to login");
      navigate("/login", { replace: true });
    },
  });
}

export function useMe() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: authApi.getMe,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export const useIsAuthenticated = () =>
  useAuthStore((s) => s.isAuthenticated);

export const useIsHydrated = () =>
  useAuthStore((s) => s.isHydrated);

export function useAuth() {
  return useAuthStore(
    useShallow((s) => ({
      isAuthenticated: s.isAuthenticated,
      isHydrated: s.isHydrated,
      user: s.user,
      account: s.account,
      roles: s.roles,
    })),
  );
}

export function useRequireAuth() {
  const { isAuthenticated, isHydrated } = useAuth();
  const navigate = useNavigate();

  if (isHydrated && !isAuthenticated) {
    navigate("/login", { replace: true });
  }

  return { isAuthenticated, isHydrated };
}

export function useIsAdmin() {
  const roles = useAuthStore((s) => s.roles);
  return roles.includes("admin");
}
